// Within this many px of the top, the header always stays revealed.
const TOP_OFFSET = 80;

/**
 * Shy header — bound to `<site-header>` in the theme layout. Owns:
 *  - `--headerHeight` on <body>, kept in sync via ResizeObserver, and
 *  - the "shy" behaviour: the header hides (CSS `.js-shy` → `-translate-y-full`)
 *    while scrolling down and reveals again on scroll up or near the top.
 *
 * Header chrome that depends on pencil-bar visibility (top padding, top corner
 * radius) keys off `.js-pencil-visible` on <html>, which the pencil bar owns —
 * see `modules/pencilBar.js`. The header stays flush + square-cornered whenever
 * that class is absent (scrolled away, dismissed, or no pencil bar at all).
 */
export default () => ({
  lastY: 0,
  shy: false,
  ticking: false,
  onScroll: null,
  resizeObserver: null,

  init() {
    const setHeaderHeight = () => {
      document.body.style.setProperty('--headerHeight', `${this.$el.offsetHeight}px`);
    };
    setHeaderHeight();
    this.resizeObserver = new ResizeObserver(setHeaderHeight);
    this.resizeObserver.observe(this.$el);

    this.lastY = window.scrollY;
    this.onScroll = () => {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.update();
        this.ticking = false;
      });
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.update();
  },

  destroy() {
    if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    this.resizeObserver?.disconnect();
    document.documentElement.classList.remove('js-shy');
  },

  update() {
    const y = window.scrollY;
    const goingDown = y > this.lastY;

    let shy = this.shy;
    if (y <= TOP_OFFSET) {
      shy = false; // at the top: always reveal
    } else if (goingDown && y > this.$el.offsetHeight) {
      shy = true; // scrolling down past the header: hide
    } else if (!goingDown) {
      shy = false; // scrolling up: reveal
    }

    if (shy !== this.shy) {
      this.shy = shy;
      document.documentElement.classList.toggle('js-shy', shy);
      // Let the header close any open menus when it slides out of view.
      if (shy) window.dispatchEvent(new CustomEvent('header:shy'));
    }

    this.lastY = y;
  },
});
